import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import ConsultantDTO from '@src/adapters/dto/consultant/ConsultantDTO';
import { useSnackbar } from 'notistack';
import useCreateConsultantLazy from '@src/adapters/hooks/consultants/useCreateConsultant';
import ConsultantNewDTO from '@src/adapters/dto/consultant/ConsultantNewDTO';
import HospitalsAutoComplete from '@src/components/hospital/HosptialsAutoComplete';
import HospitalDTO from '@src/adapters/dto/hospital/HospitalDTO';
import SpecialityAutoComplete from '@src/components/auto-complete/SpecialityAutoComplete';
import IdNameDto from '@src/adapters/dto/shared/IdNameDto';

const PREFIX = 'UserEdit';

const classes = {
  container: `${PREFIX}-container`,
  forgottenPassword: `${PREFIX}-forgottenPassword`,
  logo: `${PREFIX}-logo`,
  instructions: `${PREFIX}-instructions`,
  header: `${PREFIX}-header`,
};

const StyledContainer = styled(Container)(({ theme }) => ({
  [`&.${classes.container}`]: {},

  [`& .${classes.forgottenPassword}`]: {
    display: 'flex',
    justifyContent: 'flex-end',
  },

  [`& .${classes.logo}`]: {
    marginBottom: '20px',
  },

  [`& .${classes.instructions}`]: {
    marginBottom: '20px',
  },

  [`& .${classes.header}`]: { paddingBottom: '20px' },
}));

interface Props {
  open: boolean;
  cancel(): void;
  saved(consultant: ConsultantDTO): void;
}

function Add({ open, cancel, saved }: Props) {
  const { execute: createConsultant } = useCreateConsultantLazy();
  const { enqueueSnackbar } = useSnackbar();
  const [hospitals, setHospitals] = useState<string[]>([]);
  const [speciality, setSpeciality] = useState<IdNameDto | undefined>();
  const { handleSubmit, control, formState, reset } = useForm<ConsultantDTO>({
    mode: 'all',
  });

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      let newConsultant: ConsultantNewDTO = {
        title: data.title,
        firstName: data.firstName,
        lastName: data.lastName,
        gmcReference: data.gmcReference,
        hospitals: hospitals,
        specialityId: speciality ? speciality.id : undefined,
      };
      const savedUser = await createConsultant({ consultant: newConsultant });
      saved(savedUser.consultant);
    } catch (err: any) {
      enqueueSnackbar(err.message ? err.message : err, { variant: 'error' });
    }
  });

  const handleSelectHospitals = (updatedHospitals: HospitalDTO[]) => {
    setHospitals(updatedHospitals.map((h) => h.id));
  };

  const handleSpecialitySelected = (selectedItem: IdNameDto | null) => {
    if (selectedItem) {
      setSpeciality(selectedItem);
    } else {
      setSpeciality(undefined);
    }
  };

  return (
    <StyledContainer className={classes.container}>
      <Box>
        <Typography variant="h5" className={classes.header}>
          Details
        </Typography>
        <form onSubmit={onSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name="title"
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} label="Title" variant="standard" />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="First Name"
                        variant="standard"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Last Name"
                        variant="standard"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="gmcReference"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="GMC Reference"
                        variant="standard"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <HospitalsAutoComplete
                    selection={handleSelectHospitals}
                    selectedIds={hospitals && hospitals.map((h) => h)}
                    all={true}
                  />
                </Grid>
                <Grid item xs={12}>
                  <SpecialityAutoComplete
                    label="Speciality"
                    selection={handleSpecialitySelected}
                    selected={speciality}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={4}>
              <Button
                color="secondary"
                fullWidth
                variant="outlined"
                onClick={cancel}>
                cancel
              </Button>
            </Grid>
            <Grid item xs={4}>
              <Button
                color="primary"
                fullWidth
                type="submit"
                variant="contained"
                disabled={!formState.isValid}>
                save
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </StyledContainer>
  );
}

export default Add;
